// React provider that owns a list of concurrently-connected FTMS device
// sessions and exposes an "active" pointer for the currently-feeding device.
//
// Device sessions themselves are plain imperative objects (see
// ftmsDeviceSession.js) — that lets us run an arbitrary number of them
// without violating the rules of hooks. The provider re-renders whenever any
// session announces a state change via its onChange callback.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FTMS_SERVICE_UUID, classifyServer } from "../utils/ftms";
import { HEART_RATE_SERVICE_UUID } from "../utils/ble/constants";
import { needsPicker, pickerFilters } from "../utils/ble/reconnect";
import { DEVICE_INFORMATION_UUID } from "../utils/ble/deviceInfo";
import {
  createFtmsDeviceSession,
  listPersistedDevices,
  writePersistedDevice,
} from "../hooks/ftmsDeviceSession";
import {
  createBleHrSession,
  readPersistedHrm,
  writePersistedHrm,
} from "../hooks/bleHrSession";
import { useAuth } from "./AuthContext";
import { getBluetooth } from "../platform/bluetooth";
import { getBrowserKey } from "../utils/browserKey";
import { deviceSignature, mergeRoster } from "../utils/equipmentRoster";
import {
  getUserEquipment,
  saveUserEquipment,
  setUserEquipmentLabel,
  deleteUserEquipment,
} from "../services/api";
import {
  displayName,
  gearIdentity,
  mergeServerLabels,
  readLabels,
  setLocalLabel,
} from "../utils/equipmentLabels";

// Web Bluetooth in a browser, the native plugin inside the Android app
// (platform/bluetooth.js); both have the same shape.
const isSupported = () => !!getBluetooth();

// Chrome on Android keeps getDevices() behind chrome://flags, and without it
// the browser can't hand remembered gear back to us: the only way back in is
// the picker, which needs a tap (#169). The Android app always can.
const canListDevices = () => typeof getBluetooth()?.getDevices === "function";

// Mid-workout retry cadence for remembered gear that isn't connected.
const BACKGROUND_RETRY_MS = 15000;
// How long a reconnect pass listens for remembered gear before giving up:
// long enough to switch a bike on or put a strap on, and inside the
// mid-workout retry cadence for the quiet passes.
const SCAN_MS = 20000;
const QUIET_SCAN_MS = 8000;

const ConnectedEquipmentContext = createContext(null);

const NO_DEVICE_VIEW = Object.freeze({
  id: null,
  name: null,
  type: null,
  status: "idle",
  hint: null,
  error: null,
  metricsRef: { current: null },
  subscribe: () => () => {},
  summaryRef: { current: null },
});

export function ConnectedEquipmentProvider({ children }) {
  const sessionsRef = useRef(new Map());
  const hrRef = useRef(null);
  const activeRef = useRef(false); // workout-active gate; mutated by setActive()
  const reconnectingRef = useRef(false); // guards concurrent reconnectPersisted
  const rerunReconnectRef = useRef(false); // false | "quiet" | "loud": a pass asked for while one was busy
  const reconnectFnRef = useRef(null); // latest reconnectPersisted, for the rerun
  const [tick, setTick] = useState(0);
  const [workoutActive, setWorkoutActiveState] = useState(false);
  const [activeDeviceId, setActiveDeviceId] = useState(null);
  const [override, setOverride] = useState(null); // { deviceId, untilPhase }
  const [pairError, setPairError] = useState(null);
  const [pairing, setPairing] = useState(false);
  const [hrPairing, setHrPairing] = useState(false);
  const [hrPairError, setHrPairError] = useState(null);
  // Account roster: gear saved against the user rather than this browser.
  const [foreignDevices, setForeignDevices] = useState([]);
  // Ids getDevices() last handed back; null until a pass has asked. A
  // remembered device missing from it lost its permission when Chrome closed.
  const [grantedIds, setGrantedIds] = useState(null);
  const rosterIdsRef = useRef(new Map()); // device_key -> user_equipment.id
  // The user's own names for their gear (see utils/equipmentLabels).
  const [labels, setLabels] = useState(readLabels);

  const { isAuthenticated } = useAuth();

  const bump = useCallback(() => setTick((t) => t + 1), []);

  const sessions = sessionsRef.current;

  // Forget other copies of this machine saved in this browser under older
  // ids (not ones connected now); returns the ids dropped.
  const dropOlderCopies = useCallback(
    (keepId, name, type) => {
      const dropped = [];
      for (const p of listPersistedDevices()) {
        if (p.id !== keepId && p.name === name && p.type === type && !sessions.has(p.id)) {
          writePersistedDevice(p.id, null);
          dropped.push(p.id);
        }
      }
      return dropped;
    },
    [sessions],
  );

  // ─── Account roster ────────────────────────────────────────────────
  //
  // localStorage stays the source of truth for reconnecting — it holds the
  // Web Bluetooth ids this browser was actually granted. The server copy is a
  // backup that survives a storage wipe or a reinstall, and the carrier that
  // lets another phone list the same gear by name. Every call is
  // fire-and-forget: pairing must keep working signed-out and offline.

  const rememberEquipment = useCallback(
    (payload) => {
      if (!isAuthenticated) return;
      saveUserEquipment({ ...payload, browser_key: getBrowserKey() })
        .then((row) => {
          if (row?.id && row?.device_key) {
            rosterIdsRef.current.set(row.device_key, row.id);
          }
        })
        .catch(() => {
          /* roster is best-effort — the local pairing already succeeded */
        });
    },
    [isAuthenticated],
  );

  const forgetRemoteEquipment = useCallback(
    async (deviceKey) => {
      if (!isAuthenticated) return;
      let rowId = rosterIdsRef.current.get(deviceKey);
      rosterIdsRef.current.delete(deviceKey);
      if (!rowId) {
        // The roster hasn't been fetched yet this session (first load still in
        // flight, or it failed while offline). Look the row up rather than skip
        // it — a missed delete means the next fetch resurrects gear the user
        // has just forgotten.
        try {
          const rows = await getUserEquipment();
          rowId = rows?.find(
            (r) => r.device_key === deviceKey && r.browser_key === getBrowserKey(),
          )?.id;
        } catch {
          return;
        }
        if (!rowId) return;
      }
      deleteUserEquipment(rowId).catch(() => {});
    },
    [isAuthenticated],
  );

  const removeSession = useCallback(
    async (id) => {
      const s = sessions.get(id);
      if (!s) return;
      await s.disconnect();
      sessions.delete(id);
      if (activeDeviceId === id) {
        const next = sessions.values().next().value;
        setActiveDeviceId(next ? next.id : null);
      }
      bump();
    },
    [activeDeviceId, bump, sessions],
  );

  const forgetSession = useCallback(
    async (id) => {
      const s = sessions.get(id);
      if (s) await s.forget();
      sessions.delete(id);
      forgetRemoteEquipment(id).catch(() => {});
      if (activeDeviceId === id) {
        const next = sessions.values().next().value;
        setActiveDeviceId(next ? next.id : null);
      }
      bump();
    },
    [activeDeviceId, bump, sessions, forgetRemoteEquipment],
  );

  const addDevice = useCallback(async (opts) => {
    if (!isSupported()) {
      setPairError("Web Bluetooth not supported in this browser.");
      return null;
    }
    setPairError(null);
    setPairing(true);
    let device;
    try {
      device = await getBluetooth().requestDevice({
        filters: pickerFilters(FTMS_SERVICE_UUID, opts),
        // The maker and model, saved with each session (utils/ble/deviceInfo).
        optionalServices: [DEVICE_INFORMATION_UUID],
      });
    } catch (err) {
      setPairing(false);
      if (err?.name !== "NotFoundError") {
        setPairError(err?.message || "Pairing cancelled.");
      }
      return null;
    }

    try {
      const server = await device.gatt.connect();
      const { type } = await classifyServer(server);
      const name = device.name || "Device";

      const session = createFtmsDeviceSession({
        id: device.id,
        name,
        type,
        isActive: () => activeRef.current,
        onChange: bump,
      });
      sessions.set(session.id, session);
      await session.connect(device);
      // A session still open under an old id ("Pair again") gives way.
      let replaced = null;
      for (const [sid, old] of sessions) {
        if (sid !== session.id && old.name === name && old.type === type) {
          old.disconnect().catch(() => {});
          sessions.delete(sid);
          replaced = sid;
        }
      }
      // Without persistent permissions Chrome gives the same gear a new id
      // each time it's picked, so the copy remembered under the old id would
      // linger as a second "not in range" row, here and in the account
      // roster that brings it back on the next load (#178).
      for (const oldId of dropOlderCopies(session.id, name, type)) {
        forgetRemoteEquipment(oldId).catch(() => {});
      }
      rememberEquipment({
        kind: "ftms",
        equipment_type: type,
        name,
        device_key: session.id,
      });

      if (!activeDeviceId || activeDeviceId === replaced) setActiveDeviceId(session.id);
      bump();
      return session.id;
    } catch (err) {
      console.warn("[ConnectedEquipment] add failed", err);
      setPairError(err?.message || "Could not connect to that device.");
      return null;
    } finally {
      setPairing(false);
    }
  }, [activeDeviceId, bump, sessions, rememberEquipment, forgetRemoteEquipment, dropOlderCopies]);

  /**
   * Manually select the active device. Optionally record an override that
   * pins this choice for the current workout phase (preventing the
   * auto-switch effect from undoing the user's tap on the next phase tick).
   */
  const setActive = useCallback(
    (id, { phaseId = null, auto = false } = {}) => {
      if (!sessions.has(id)) return;
      setActiveDeviceId(id);
      if (!auto && phaseId) {
        setOverride({ deviceId: id, untilPhase: phaseId });
      }
    },
    [sessions],
  );

  const clearOverride = useCallback(() => setOverride(null), []);

  // ─── HR monitor (direct BLE 0x180D) ────────────────────────────────
  //
  // HR is global per-human and persists across equipment switches /
  // floor-work gaps, so it lives outside the FTMS device list. We hold at
  // most one HR session at a time — pairing a new strap replaces any
  // existing one.

  const addHrMonitor = useCallback(async (opts) => {
    if (!isSupported()) {
      setHrPairError("Web Bluetooth not supported in this browser.");
      return null;
    }
    setHrPairError(null);
    setHrPairing(true);
    let device;
    try {
      device = await getBluetooth().requestDevice({
        filters: pickerFilters(HEART_RATE_SERVICE_UUID, opts),
      });
    } catch (err) {
      setHrPairing(false);
      if (err?.name !== "NotFoundError") {
        setHrPairError(err?.message || "Pairing cancelled.");
      }
      return null;
    }

    try {
      // Replace any existing HR session.
      if (hrRef.current) {
        await hrRef.current.disconnect();
      }
      const session = createBleHrSession({
        id: device.id,
        name: device.name || "HR Monitor",
        isActive: () => activeRef.current,
        onChange: bump,
      });
      hrRef.current = session;
      await session.connect(device);
      rememberEquipment({
        kind: "hrm",
        name: session.name,
        device_key: session.id,
      });
      bump();
      return session.id;
    } catch (err) {
      console.warn("[ConnectedEquipment] HR pair failed", err);
      setHrPairError(err?.message || "Could not connect to that strap.");
      return null;
    } finally {
      setHrPairing(false);
    }
  }, [bump, rememberEquipment]);

  const forgetHrMonitor = useCallback(async () => {
    if (hrRef.current) {
      const key = hrRef.current.id;
      await hrRef.current.forget();
      hrRef.current = null;
      forgetRemoteEquipment(key).catch(() => {});
      bump();
    }
  }, [bump, forgetRemoteEquipment]);

  // Reconnect persisted devices that the browser still has permission for
  // (Chrome 113+ `getDevices()`). Re-triggerable — unlike the old mount-only
  // pass, this can be re-run at the moments a device is actually likely to be
  // powered on (workout start, tab refocus), which is the real fix for "I have
  // to reconnect every session": the bike is usually still off at app load.
  // Idempotent — it reuses existing (possibly errored) sessions and retries
  // their connect(), and never duplicates a session. `quiet` is the
  // mid-workout timer below: existing sessions retry without flashing
  // "Connecting…" or replacing their status when the device still isn't there.
  // `skip`: ids the pass just before this one already listened for, so a
  // queued rerun doesn't start a second long scan for gear that's off.
  const reconnectPersisted = useCallback(async ({ quiet = false, skip = null } = {}) => {
    if (!canListDevices()) return;
    // A pass asked for mid-flight isn't dropped: the roster fetch can land
    // while the mount pass is still working through GATT connects, and the
    // devices it just restored would otherwise wait for the next tab focus.
    if (reconnectingRef.current) {
      // A user-facing pass outranks a quiet one when both queue up.
      if (rerunReconnectRef.current !== "loud") rerunReconnectRef.current = quiet ? "quiet" : "loud";
      return;
    }
    reconnectingRef.current = true;
    const attempted = new Set();
    try {
      const persisted = listPersistedDevices();
      const persistedHr = readPersistedHrm();
      if (!persisted.length && !persistedHr) return;
      let known;
      try {
        known = await getBluetooth().getDevices();
      } catch {
        return;
      }
      setGrantedIds(new Set(known.map((d) => d.id)));
      // Each device listens for itself (up to scanMs), so a bike that's off
      // doesn't hold up the strap.
      const scanMs = quiet ? QUIET_SCAN_MS : SCAN_MS;
      const machines = persisted.map(async (p) => {
        const device = known.find((d) => d.id === p.id);
        if (!device || skip?.has(p.id)) return;
        attempted.add(p.id);
        let session = sessions.get(p.id);
        const existed = !!session;
        if (!session) {
          session = createFtmsDeviceSession({
            id: p.id,
            name: p.name || device.name || "Device",
            type: p.type,
            isActive: () => activeRef.current,
            onChange: bump,
          });
          sessions.set(p.id, session);
        }
        if (session.status !== "connected" && session.status !== "connecting") {
          try {
            if (quiet && existed) {
              if (!(await session.retry(device, { scanMs }))) return;
            } else {
              await session.connect(device, { scanMs });
            }
            rememberEquipment({
              kind: "ftms",
              equipment_type: p.type,
              name: session.name,
              device_key: p.id,
            });
            // connect() reports a miss through status, not by throwing: only
            // gear that answered may feed the workout.
            if (session.status === "connected") setActiveDeviceId((curr) => curr ?? p.id);
          } catch {
            /* leave session in error state — user can retry / re-pair */
          }
        }
        bump();
      });

      // Persisted HRM.
      const strap = (async () => {
        if (!persistedHr || skip?.has(persistedHr.id)) return;
        attempted.add(persistedHr.id);
        const device = known.find((d) => d.id === persistedHr.id);
        if (device && (!hrRef.current || hrRef.current.status !== "connected")) {
          let session = hrRef.current;
          const existed = !!session;
          if (!session) {
            session = createBleHrSession({
              id: persistedHr.id,
              name: persistedHr.name || device.name || "HR Monitor",
              isActive: () => activeRef.current,
              onChange: bump,
            });
            hrRef.current = session;
          }
          try {
            if (quiet && existed) {
              if (!(await session.retry(device, { scanMs }))) throw new Error("not back yet");
            } else {
              await session.connect(device, { scanMs });
            }
            rememberEquipment({
              kind: "hrm",
              name: session.name,
              device_key: persistedHr.id,
            });
          } catch {
            /* leave in error state */
          }
          bump();
        }
      })();

      await Promise.all([...machines, strap]);
    } finally {
      reconnectingRef.current = false;
      const rerun = rerunReconnectRef.current;
      if (rerun) {
        rerunReconnectRef.current = false;
        await reconnectFnRef.current?.({ quiet: rerun === "quiet", skip: attempted });
      }
    }
  }, [bump, sessions, rememberEquipment]);

  useEffect(() => {
    reconnectFnRef.current = reconnectPersisted;
  }, [reconnectPersisted]);

  /**
   * Pull the account roster and fold it into this browser.
   *
   * Rows paired here are written straight back into localStorage, so the
   * silent-reconnect path above finds them exactly as if they had never been
   * lost — this is what makes a cleared cache or a fresh install recoverable.
   * Rows paired on another phone can't reconnect here (their Web Bluetooth ids
   * mean nothing in this browser profile), so they are surfaced by name for a
   * one-tap re-pair instead.
   */
  const loadRoster = useCallback(async () => {
    if (!isAuthenticated) {
      // Signed out: the account roster is no longer ours to show. The devices
      // paired in this browser stay in localStorage and keep working.
      rosterIdsRef.current = new Map();
      setForeignDevices((curr) => (curr.length ? [] : curr));
      return;
    }
    let rows;
    try {
      rows = await getUserEquipment();
    } catch {
      return; // offline or signed out mid-flight — localStorage still stands
    }
    if (!Array.isArray(rows)) return;
    setLabels((curr) => mergeServerLabels(curr, rows));

    rosterIdsRef.current = new Map(
      rows.filter((r) => r?.device_key).map((r) => [r.device_key, r.id]),
    );

    const localDevices = Array.from(sessions.values()).map((sess) => ({
      kind: "ftms",
      name: sess.name,
      type: sess.type,
    }));
    if (hrRef.current) {
      localDevices.push({
        kind: "hrm",
        name: hrRef.current.name,
        type: "hr_monitor",
      });
    }

    const { hydrate, foreign, stale } = mergeRoster({
      rows,
      browserKey: getBrowserKey(),
      localDevices,
    });

    // The same bike saved under an older id lists twice and its "Forget"
    // only forgets one copy (#178). Drop the old rows here and on the server,
    // unless that id is the one connected right now.
    for (const d of stale) {
      if (sessions.has(d.id) || hrRef.current?.id === d.id) continue;
      if (d.kind === "ftms") writePersistedDevice(d.id, null);
      else if (readPersistedHrm()?.id === d.id) writePersistedHrm(null);
      rosterIdsRef.current.delete(d.id);
      deleteUserEquipment(d.rowId).catch(() => {});
    }

    for (const d of hydrate) {
      if (d.kind === "hrm") {
        if (!readPersistedHrm()) writePersistedHrm({ id: d.id, name: d.name });
      } else if (d.type) {
        writePersistedDevice(d.id, { id: d.id, name: d.name, type: d.type });
        dropOlderCopies(d.id, d.name, d.type);
      }
    }

    setForeignDevices(foreign);
    if (hydrate.length) await reconnectPersisted();
  }, [isAuthenticated, sessions, reconnectPersisted, dropOlderCopies]);

  const setWorkoutActive = useCallback(
    (next) => {
      const prev = activeRef.current;
      activeRef.current = !!next;
      setWorkoutActiveState(!!next);
      if (next && !prev) {
        // Workout just started — clear any leftover session aggregates, and
        // take a fresh reconnect shot now that the user is at the machine.
        for (const s of sessions.values()) s.resetSession();
        reconnectPersisted();
      }
    },
    [sessions, reconnectPersisted],
  );

  // Silent reconnect on mount, and again whenever the tab returns to the
  // foreground (the user often powers the device on after opening the app).
  // Sessions don't repaint while hidden (#189), so repaint on the way back.
  useEffect(() => {
    if (!isSupported()) return undefined;
    reconnectPersisted();
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      bump();
      reconnectPersisted();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reconnectPersisted, bump]);

  // While a workout runs, keep trying any remembered gear that isn't
  // connected. The one-shot passes above miss a strap that's put on (or a bike
  // switched on) a minute in, and a mid-session drop gives up after ~13 s of
  // backoff. Outside workouts we stay idle to spare the battery.
  useEffect(() => {
    if (!workoutActive || !isSupported()) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") reconnectPersisted({ quiet: true });
    }, BACKGROUND_RETRY_MS);
    return () => clearInterval(timer);
  }, [workoutActive, reconnectPersisted]);

  // The roster arrives with (or shortly after) the session, so re-run on sign
  // in rather than on mount alone.
  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  // Final teardown on unmount.
  useEffect(() => {
    return () => {
      for (const s of sessions.values()) {
        s.disconnect().catch(() => {});
      }
      sessions.clear();
      if (hrRef.current) {
        hrRef.current.disconnect().catch(() => {});
        hrRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Name a piece of gear ("C2 Rower"), or clear the name with an empty string.
   * `gear` is any view from this context; its advertisedName is what the
   * name is keyed on, so the same gear is recognised everywhere.
   */
  const setEquipmentLabel = useCallback(
    async (gear, label) => {
      const identity = gearIdentity({ ...gear, name: gear.advertisedName || gear.name });
      setLabels((curr) => setLocalLabel(curr, identity, label));
      if (!isAuthenticated) return;
      await setUserEquipmentLabel({
        kind: identity.kind,
        equipment_type: identity.kind === "hrm" ? null : identity.type,
        name: identity.name,
        label,
      });
    },
    [isAuthenticated],
  );

  const value = useMemo(() => {
    // Views show the user's name for the gear; advertisedName is what the
    // device calls itself, which is what identifies it.
    const named = (item, kind) =>
      item && {
        ...item,
        advertisedName: item.name,
        name: displayName(labels, gearIdentity({ kind, type: item.type, name: item.name })),
      };
    const viewOf = (s) => ({
      id: s.id,
      name: displayName(labels, { kind: "ftms", type: s.type, name: s.name }),
      advertisedName: s.name,
      type: s.type,
      status: s.status,
      error: s.error,
      hint: s.hint,
      snapshot: s.snapshot,
      metricsRef: s.metricsRef,
      subscribe: s.subscribe,
      summaryRef: s.summaryRef,
      // Used by the telemetry recorder to discard stale "zombie" readings
      // when a device has silently dropped notifications.
      getFreshSnapshot: s.getFreshSnapshot,
    });
    const devices = Array.from(sessions.values()).map(viewOf);
    const active = devices.find((d) => d.id === activeDeviceId) || null;

    // Devices we remember but the browser can't currently surface as a session
    // at all (cache miss) — offered as one-tap "Reconnect" entries in the UI.
    const persisted = listPersistedDevices();
    const persistedHr = readPersistedHrm();

    // An account row paired on another phone is only worth showing while this
    // browser has no copy of the same gear — pair it here and the named
    // "pair here" row folds away without waiting for the next roster fetch.
    const known = new Set();
    for (const sess of sessions.values()) {
      known.add(
        deviceSignature({ kind: "ftms", name: sess.name, type: sess.type }),
      );
    }
    for (const p of persisted) {
      known.add(deviceSignature({ kind: "ftms", name: p.name, type: p.type }));
    }
    for (const h of [hrRef.current, persistedHr]) {
      if (h) {
        known.add(
          deviceSignature({ kind: "hrm", name: h.name, type: "hr_monitor" }),
        );
      }
    }
    const visibleForeign = foreignDevices.filter(
      (d) => !known.has(deviceSignature(d)),
    );

    // Remembered gear that only the picker can bring back (#169).
    const browser = { listable: canListDevices(), grantedIds };
    const withPicker = (d) => d && { ...d, needsPicker: needsPicker(d, browser) };

    const rememberedDevices = [
      ...persisted
        .filter((p) => !sessions.has(p.id))
        .map((p) => ({ id: p.id, name: p.name, type: p.type, kind: "ftms" })),
      ...visibleForeign.filter((d) => d.kind === "ftms"),
    ].map((d) => withPicker(named(d, "ftms")));
    const foreignHr = visibleForeign.find((d) => d.kind === "hrm") || null;
    const rememberedHr = withPicker(
      named(
        hrRef.current
          ? null
          : persistedHr
            ? { id: persistedHr.id, name: persistedHr.name, kind: "hr" }
            : foreignHr,
        "hrm",
      ),
    );
    // Gear paired in this browser that Chrome has since forgotten: what the
    // chrome://flags tip is for. Another phone's gear isn't a sign of that.
    const autoReconnectBlocked = [...rememberedDevices, rememberedHr].some(
      (d) => d && d.needsPicker && !d.foreign,
    );
    const hr = hrRef.current
      ? {
          id: hrRef.current.id,
          name: displayName(labels, { kind: "hrm", type: "hr_monitor", name: hrRef.current.name }),
          advertisedName: hrRef.current.name,
          status: hrRef.current.status,
          error: hrRef.current.error,
          hint: hrRef.current.hint,
          snapshot: hrRef.current.snapshot,
          metricsRef: hrRef.current.metricsRef,
          subscribe: hrRef.current.subscribe,
          getFreshSnapshot: hrRef.current.getFreshSnapshot,
        }
      : null;

    return {
      devices,
      activeDeviceId,
      active,
      override,
      hr,
      rememberedDevices,
      rememberedHr,
      autoReconnectBlocked,
      addDevice,
      addHrMonitor,
      forgetHrMonitor,
      reconnectPersisted,
      pairing,
      pairError,
      hrPairing,
      hrPairError,
      isSupported: isSupported(),
      removeDevice: removeSession,
      forgetDevice: forgetSession,
      setActive,
      clearOverride,
      setWorkoutActive,
      setEquipmentLabel,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tick,
    labels,
    setEquipmentLabel,
    activeDeviceId,
    foreignDevices,
    grantedIds,
    override,
    pairing,
    pairError,
    hrPairing,
    hrPairError,
    addDevice,
    addHrMonitor,
    forgetHrMonitor,
    reconnectPersisted,
    removeSession,
    forgetSession,
    setActive,
    clearOverride,
    setWorkoutActive,
  ]);

  return (
    <ConnectedEquipmentContext.Provider value={value}>
      {children}
    </ConnectedEquipmentContext.Provider>
  );
}

export function useConnectedEquipment() {
  const ctx = useContext(ConnectedEquipmentContext);
  if (!ctx) {
    throw new Error(
      "useConnectedEquipment must be used inside <ConnectedEquipmentProvider>",
    );
  }
  return ctx;
}

export function useActiveDevice() {
  const { active } = useConnectedEquipment();
  return active || NO_DEVICE_VIEW;
}
